import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  formatQty,
  MATERIAL_ORDER_STATUS_LABELS,
  type LowStockDto,
  type MaterialOrderStatus,
  type MaterialOrdersResponse,
  type OrderLineDto,
} from "@klantpunt/shared";
import { Tabs } from "../../app/Choice";
import { buildingPath } from "../../app/navItems";
import { PageHeader } from "../../app/PageHeader";
import { api } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useClientContext } from "../../lib/clientContext";
import { useToast } from "../../lib/toast";
import { priceLabel } from "./MaterialDialogs";
import { errMsg } from "./useMaterials";

type Tab = "taken" | "minimum";
type Row = OrderLineDto | LowStockDto;

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
const statusOrder: Record<MaterialOrderStatus, number> = { to_order: 0, ordered: 1, delivered: 2 };

/** Unieke keuzes voor een filter, op naam gesorteerd. */
function options<T extends { id: string }>(rows: (T | null)[], label: (x: T) => string) {
  const m = new Map<string, string>();
  for (const x of rows) if (x) m.set(x.id, label(x));
  return [...m].sort((a, b) => a[1].localeCompare(b[1], "nl"));
}

function Filter({
  label,
  value,
  onChange,
  opts,
  none,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  opts: [string, string][];
  none?: string;
}) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Alle</option>
        {opts.map(([id, l]) => (
          <option key={id} value={id}>
            {l}
          </option>
        ))}
        {none && <option value="-">{none}</option>}
      </select>
    </label>
  );
}

/**
 * Te bestellen over alle gebouwen (docs/MATERIALEN.md, fase 2): regels *Bestellen* op taken die nog niet geleverd
 * zijn, en artikelen onder het minimum. Klant via de klantkeuze bovenin; gebouw en leverancier staan in de URL.
 */
export function OrdersPage() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "minimum" ? "minimum" : "taken";
  const f = { gebouw: params.get("gebouw") ?? "", lev: params.get("lev") ?? "" };
  // Klant: de klantkeuze bovenin, net als bij de gebouwen, keuringen en de MJOP.
  const { id: clientId } = useClientContext();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const enabled = hasModule(me, "materials");
  const canOrder = can(me, "material.order");
  const data = useQuery({
    queryKey: ["material-orders"],
    queryFn: () => api<MaterialOrdersResponse>("/materials/orders"),
    enabled,
  });

  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
    setPicked(new Set());
  };

  if (!enabled)
    return (
      <div className="center">
        <p>Geen toegang tot de module Materialen.</p>
      </div>
    );

  const lines = data.data?.lines ?? [];
  const low = data.data?.lowStock ?? [];
  const all: Row[] = [...lines, ...low];
  const ofClient = (r: Row) => !clientId || r.client?.id === clientId;
  const match = (r: Row) =>
    ofClient(r) &&
    (!f.gebouw || r.building.id === f.gebouw) &&
    (!f.lev || (f.lev === "-" ? !r.supplier : r.supplier?.id === f.lev));
  const shownLines = lines
    .filter(match)
    .sort(
      (a, b) =>
        statusOrder[a.orderStatus] - statusOrder[b.orderStatus] || a.building.code.localeCompare(b.building.code),
    );
  const shownLow = low.filter(match);
  const gebouwen = options(
    all.filter(ofClient).map((r) => r.building),
    (b) => `${b.code} · ${b.name}`,
  );
  const leveranciers = options(
    all.map((r) => r.supplier),
    (s) => s.name,
  );
  const ids = shownLines.map((l) => l.id);
  const pickedIds = ids.filter((id) => picked.has(id));
  const allPicked = ids.length > 0 && pickedIds.length === ids.length;
  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function setStatus(sel: string[], orderStatus: MaterialOrderStatus) {
    setBusy(true);
    try {
      await api("/task-materials/order-status", { method: "POST", body: { ids: sel, orderStatus } });
      toast(`${sel.length === 1 ? "1 regel" : `${sel.length} regels`} op ${MATERIAL_ORDER_STATUS_LABELS[orderStatus]}`);
      setPicked(new Set());
      await qc.invalidateQueries({ queryKey: ["material-orders"] });
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const article = (r: Row, narrow: string) => (
    <>
      <Link to={`${buildingPath(r.building.id, "materialen")}?artikel=${r.material.id}`}>{r.material.name}</Link>
      <div className="muted small mat-wide">
        {[r.material.articleNo, r.material.priceCents != null && priceLabel(r.material)].filter(Boolean).join(" · ")}
      </div>
      <div className="muted small">{r.supplier?.name ?? "Geen leverancier"}</div>
      <div className="muted small mat-narrow">{narrow}</div>
    </>
  );
  const building = (r: Row) => (
    <>
      <span className="code">{r.building.code}</span> {r.building.name}
      {r.client && <div className="muted small">{r.client.name}</div>}
    </>
  );

  return (
    <div className="page dir-page mat-orders">
      <PageHeader
        label="Alle gebouwen"
        title="Te bestellen"
        tabs={
          <Tabs
            label="Te bestellen"
            value={tab}
            onChange={(v) => set("tab", v === "minimum" ? "minimum" : "")}
            items={[
              { value: "taken", label: "Voor taken", count: shownLines.length },
              { value: "minimum", label: "Onder minimum", count: shownLow.length },
            ]}
          />
        }
      />
      <div className="mjop-filters">
        <Filter label="Gebouw" value={f.gebouw} onChange={(v) => set("gebouw", v)} opts={gebouwen} />
        <Filter
          label="Leverancier"
          value={f.lev}
          onChange={(v) => set("lev", v)}
          opts={leveranciers}
          none="Geen leverancier"
        />
      </div>
      {data.isError && <p className="alert">{errMsg(data.error)}</p>}
      {data.isPending && <Loading />}
      {data.data && tab === "taken" && (
        <>
          <p className="muted small">
            Materialen die op een taak op <em>Bestellen</em> staan en nog niet geleverd zijn.
          </p>
          {shownLines.length ? (
            <div className="tscroll">
              <table className="it" aria-label="Te bestellen voor taken">
                <thead>
                  <tr>
                    {canOrder && (
                      <th scope="col" className="mat-pick">
                        <input
                          type="checkbox"
                          aria-label="Alles kiezen"
                          checked={allPicked}
                          onChange={() => setPicked(allPicked ? new Set() : new Set(ids))}
                        />
                      </th>
                    )}
                    <th scope="col">Artikel</th>
                    <th scope="col" className="num dir-hide-narrow">
                      Aantal
                    </th>
                    <th scope="col" className="dir-hide-narrow">
                      Taak
                    </th>
                    <th scope="col" className="dir-hide-narrow">
                      Gebouw
                    </th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {shownLines.map((l) => (
                    <tr key={l.id}>
                      {canOrder && (
                        <td className="mat-pick">
                          <input
                            type="checkbox"
                            aria-label={`${l.material.name} bij ${l.task.code} kiezen`}
                            checked={picked.has(l.id)}
                            onChange={() => toggle(l.id)}
                          />
                        </td>
                      )}
                      <td className="tcell-title">
                        {article(
                          l,
                          `${formatQty(l.quantity)} ${l.material.unit} · ${l.task.code} · ${l.building.code}${l.task.dueDate ? ` · uiterlijk ${fmtDate(l.task.dueDate)}` : ""}`,
                        )}
                      </td>
                      <td className="num dir-hide-narrow">
                        {formatQty(l.quantity)} {l.material.unit}
                      </td>
                      <td className="dir-hide-narrow">
                        <Link to={buildingPath(l.building.id, "taken")}>
                          <span className="code">{l.task.code}</span>
                        </Link>{" "}
                        {l.task.title}
                        {l.task.dueDate && <div className="muted small">Uiterlijk {fmtDate(l.task.dueDate)}</div>}
                      </td>
                      <td className="dir-hide-narrow">{building(l)}</td>
                      <td>
                        {canOrder ? (
                          <select
                            aria-label={`Status ${l.material.name} bij ${l.task.code}`}
                            value={l.orderStatus}
                            disabled={busy}
                            onChange={(e) => setStatus([l.id], e.target.value as MaterialOrderStatus)}
                          >
                            {(["to_order", "ordered", "delivered"] as const).map((s) => (
                              <option key={s} value={s}>
                                {MATERIAL_ORDER_STATUS_LABELS[s]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="chip">{MATERIAL_ORDER_STATUS_LABELS[l.orderStatus]}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Niets te bestellen{f.gebouw || f.lev ? " met deze filters" : ""}.</p>
          )}
          {canOrder && pickedIds.length > 0 && (
            <div className="bulkbar" role="region" aria-label="Bulkacties">
              <span className="check">
                <strong className="num">{pickedIds.length}</strong> van {ids.length} gekozen
              </span>
              <span className="acts">
                <button
                  type="button"
                  className="btn small"
                  disabled={busy}
                  onClick={() => setStatus(pickedIds, "ordered")}
                >
                  Besteld
                </button>
                <button
                  type="button"
                  className="btn small"
                  disabled={busy}
                  onClick={() => setStatus(pickedIds, "delivered")}
                >
                  Geleverd
                </button>
              </span>
            </div>
          )}
        </>
      )}
      {data.data && tab === "minimum" && (
        <>
          <p className="muted small">Artikelen waarvan minder in het gebouw ligt dan het minimum.</p>
          {shownLow.length ? (
            <div className="tscroll">
              <table className="it" aria-label="Onder minimum">
                <thead>
                  <tr>
                    <th scope="col">Artikel</th>
                    <th scope="col" className="num">
                      Voorraad
                    </th>
                    <th scope="col" className="num">
                      Tekort
                    </th>
                    <th scope="col" className="dir-hide-narrow">
                      Gebouw
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shownLow.map((r) => (
                    <tr key={`${r.building.id}:${r.material.id}`}>
                      <td className="tcell-title">
                        {article(r, `${r.building.code} ${r.building.name}`)}
                        {r.location && <div className="muted small">{r.location}</div>}
                      </td>
                      <td className="num">
                        {formatQty(r.quantity)} / {formatQty(r.minQuantity)}
                      </td>
                      <td className="num">
                        {formatQty(r.minQuantity - r.quantity)} {r.material.unit}
                        {r.onOrder > 0 && <div className="muted small">{formatQty(r.onOrder)} voor taken</div>}
                      </td>
                      <td className="dir-hide-narrow">{building(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Alles op peil{f.gebouw || f.lev ? " met deze filters" : ""}.</p>
          )}
        </>
      )}
    </div>
  );
}
